import { createMemo, For, Show } from "solid-js";
import { draftPattern } from "@tailor-frame/engine";
import { specifications } from "@tailor-frame/patterns";
import type { PrintGarment } from "./PrintPreview";
import { PrintStudio } from "./PrintStudio";
import { projectForPattern, type DraftBody } from "../core/patternSettings";
import type { PrintSet } from "../core/printSets";
import { linkClick, routePath, type SetView } from "../core/router";

/**
 * A print set's page: every garment drafted from the shared body and its own
 * saved options, then printed together as tiled sheets (easy print) or laid
 * out on the cloth (easy layout).
 */
export function PrintSetPage(props: { set: PrintSet; view: SetView; body: DraftBody; includeInlay: boolean; excludedByPattern: Record<string, string[]> }) {
  const garments = createMemo(() => props.set.members.flatMap((member): PrintGarment[] => {
    const entry = specifications.find((item) => item.specification.pattern.id === member.patternId);
    if (!entry) return [];
    const project = projectForPattern(entry.specification, props.body, props.includeInlay);
    return [{ draft: draftPattern(entry.specification, project), specification: entry.specification, project, excluded: props.excludedByPattern[member.patternId] ?? [], code: member.code, name: member.name }];
  }));
  const missing = () => props.set.members.filter((member) => !specifications.some((item) => item.specification.pattern.id === member.patternId));
  const tab = (view: SetView, label: string) => <a href={routePath({ name: "set", setId: props.set.id, view })} classList={{ selected: props.view === view }} aria-current={props.view === view ? "page" : undefined} onClick={linkClick}>{label}</a>;

  return (
    <>
      <header class="set-header no-print">
        <div><p class="eyebrow">PRINT SET</p><h2>{props.set.name}</h2><p>{props.set.description}</p></div>
        <nav class="segmented-control set-tabs" aria-label="Print set view">{tab("workspace", "Workspace")}{tab("print", "Print")}</nav>
        <ul class="set-members">
          <For each={props.set.members}>{(member) => <li><strong>{member.code}</strong> <a href={routePath({ name: "set", setId: props.set.id, view: "workspace", patternId: member.patternId })} onClick={linkClick} title="Open to change its style or leave pieces out">{member.name}</a></li>}</For>
        </ul>
      </header>
      <Show when={missing().length > 0}><p class="print-warning no-print">Not in the library: {missing().map((member) => member.patternId).join(", ")}.</p></Show>
      <Show when={garments().length > 0}>
        {/* The old layout address opens the print step arranged for fabric. */}
        <PrintStudio garments={garments()} title={props.set.name} arrange={props.view === "layout" ? "fabric" : undefined} />
      </Show>
    </>
  );
}
